import { Route } from '@angular/router';
import { DriverDataResolver, InitialDataResolver, OfficeDataResolver, WebsiteSettingsDataResolver } from '@app/app.resolvers';
import * as profileSettings from '@auth/profile/profile.settings.json';
import * as confirmMembershipSettings from '@confirm-membership/confirm-membership.json';
import { AuthGuard, DriverAuthGuard, OfficeAuthGuard } from '@core/auth/guards/auth.guard';
import { NoAuthGuard } from '@core/auth/guards/noAuth.guard';
import * as chatSettings from '@customer/chat/chat.settings.json';
import { PAGE_SETTINGS } from '@shared/components/page-layout/page-layout.types';
import { Role } from '@shared/enums/roles.enum';

export const guestsRoute: Route = {
    canMatch: [NoAuthGuard],
    loadComponent: () => import('@layout/layout.component').then(m => m.LayoutComponent),
    data: {
        layout: 'empty'
    },
    loadChildren: () => import('@auth/auth.routes').then(m => m.guestsRoute),
}

export const authenticatedRoute = {
    canMatch: [AuthGuard],
    loadComponent: () => import('@layout/layout.component').then(m => m.LayoutComponent),
    data: {
        layout: 'empty'
    },
    children: [
        { 
            path: 'sign-out', 
            loadComponent: () => import('@auth/sign-out/sign-out.component').then(m => m.AuthSignOutComponent) 
        },
        { 
            path: 'confirm-membership',
            resolve: { websiteSettings: WebsiteSettingsDataResolver },
            providers: [
                {
                    provide: PAGE_SETTINGS,
                    useValue: confirmMembershipSettings
                },
            ],
            loadChildren: () => import('@confirm-membership/confirm-membership.routing').then(m => m.confirmMembershipRoutes) 
        },
        { 
            path: 'unlock-session', 
            loadChildren: () => import('@auth/unlock-session/unlock-session.routing').then(m => m.authUnlockSessionRoutes) 
        },
        {
            path: 'oauth/consent',
            loadChildren: () => import('@auth/oauth-consent/oauth-consent.routing').then(m => m.oauthConsentRoutes)
        },
        {
            path: 'reload',
            loadComponent: () => import('@reload/reload.component').then(m => m.ReloadComponent)
        },
    ]
}

export const landingRoute = {
    loadComponent: () => import('@layout/layout.component').then(m => m.LayoutComponent),
    resolve: {
        initialData: InitialDataResolver,
        websiteSettings: WebsiteSettingsDataResolver,
    },
    children: [
        { 
            path: 'home', 
            loadChildren: () => import('@landing/home/home.routing').then(m => m.landingHomeRoutes)
        },
        { 
            path: 'help', 
            loadChildren: () => import('@help-center/help-center.routing').then(m => m.helpCenterRoutes)
        },
        { 
            path: 'profile',
            providers: [
                {
                    provide: PAGE_SETTINGS,
                    useValue: profileSettings
                },
            ],
            loadChildren: () => import('@auth/profile/profile.routing').then(m => m.profileRoutes)
        },
        { 
            path: 'rating', 
            loadChildren: () => import('@shared/components/job-checkout/job-rating/job-rating.routing').then(m => m.jobRatingRoutes)
        },
        { 
            path: 'public', 
            loadChildren: () => import('@public/public.routing').then(m => m.publicRoutes) 
        },
        { 
            path: 'chat',
            providers: [
                {
                    provide: PAGE_SETTINGS,
                    useValue: chatSettings
                },
            ],
            loadChildren: () => import('@customer/chat/customer-chat.routing').then(m => m.customerChatRoutes)
        },
    ]
}

export const customerRoute = {
    canMatch: [AuthGuard],
    loadComponent: () => import('@layout/layout.component').then(m => m.LayoutComponent),
    resolve: {
        initialData: InitialDataResolver,
        websiteSettings: WebsiteSettingsDataResolver,
    },
    loadChildren: () => import('@customer/customer.routes').then(m => m.customerRoutes)
}

export const officeRoute = {
    canMatch: [OfficeAuthGuard],
    loadComponent: () => import('@layout/layout.component').then(m => m.LayoutComponent),
    resolve: {
        initialData: OfficeDataResolver,
        websiteSettings: WebsiteSettingsDataResolver,
    },
    loadChildren: () => import('@office/office.routes').then(m => m.officeRoutes)
}

export const driverRoute = {
    canMatch: [DriverAuthGuard],
    loadComponent: () => import('@layout/layout.component').then(m => m.LayoutComponent),
    resolve: {
        initialData: DriverDataResolver,
        websiteSettings: WebsiteSettingsDataResolver,
    },
    providers: [
        {
            provide: Role,
            useValue: Role.Driver
        }
    ],
    loadChildren: () => import('@driver/driver.routing').then(m => m.driverRoutes)
}

export const notAuthorized = {
    canMatch: [AuthGuard],
    loadChildren: () => import('@error/error-403/error-403.routing').then(m => m.errorRoutes)
}

export const undefinedRoutes = {
    canMatch: [AuthGuard],
    loadChildren: () => import('@error/error-404/error-404.routing').then(m => m.errorRoutes)
}